import { deburr } from 'lodash';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { ROUTES } from '@futrli/shared-services';

import { ConsolidatedForecastReportStructureWithTabs } from '../../../../core/types';
import { PREDICTIONS_SUB_PAGES_IDS } from './const';
import { usePredictionsInfoContext } from '../../context/predictions-info-context';
import { usePredictManagementContext } from '../../../../predict-management/context/predict-management-context';
import { useSubTabNameMaps } from '../hooks/useSubTabNameMaps';

export const useForecastingPages = () => {
  const intl = useIntl();
  const { financialConfig } = usePredictManagementContext();
  const { forecastReportV2: forecastReport, isConsolidation } = usePredictionsInfoContext();
  const subtabs = useMemo(
    () => (forecastReport?.structure as ConsolidatedForecastReportStructureWithTabs)?.tabs || [],
    [forecastReport]
  );
  const { tabNames } = useSubTabNameMaps();

  const forecastingPages = useMemo(() => {
    if (isConsolidation) {
      return [
        {
          id: PREDICTIONS_SUB_PAGES_IDS.PREDICTIONS_INVOICES,
          route: ROUTES.INVOICES,
          linkComponent: intl.formatMessage({ id: 'predict.invoices', defaultMessage: 'Invoices' })
        },
        {
          id: PREDICTIONS_SUB_PAGES_IDS.PREDICTIONS_SALES,
          route: ROUTES.SALES,
          linkComponent: intl.formatMessage({ id: 'financial_fields.category.sales', defaultMessage: 'Sales' })
        },
        {
          id: PREDICTIONS_SUB_PAGES_IDS.PREDICTIONS_COSTS,
          route: ROUTES.COSTS,
          linkComponent: intl.formatMessage({ id: 'predict.predictions.costs', defaultMessage: 'Costs' })
        },
        {
          id: PREDICTIONS_SUB_PAGES_IDS.PREDICTIONS_EXPENSES,
          route: ROUTES.EXPENSES,
          linkComponent: intl.formatMessage({ id: 'financial_fields.category.expenses', defaultMessage: 'Expenses' })
        },
        {
          id: PREDICTIONS_SUB_PAGES_IDS.PREDICTIONS_OTHER_P_AND_L,
          route: ROUTES.OTHER_P_AND_L,
          linkComponent: intl.formatMessage({ id: 'predict.predictions.other_p_and_l', defaultMessage: 'Other P&L' })
        },
        {
          id: PREDICTIONS_SUB_PAGES_IDS.PREDICTIONS_ASSETS,
          route: ROUTES.ASSETS,
          linkComponent: intl.formatMessage({ id: 'predict.predictions.assets', defaultMessage: 'Assets' })
        },
        {
          id: PREDICTIONS_SUB_PAGES_IDS.PREDICTIONS_LIABILITIES,
          route: ROUTES.LIABILITIES,
          linkComponent: intl.formatMessage({ id: 'predict.predictions.liabilities', defaultMessage: 'Liabilities' })
        },
        {
          id: PREDICTIONS_SUB_PAGES_IDS.PREDICTION_PROFIT_LOSS,
          route: ROUTES.PROFIT_AND_LOSS,
          linkComponent: intl.formatMessage({
            id: 'financial_fields.type.profit_and_loss',
            defaultMessage: 'Profit & Loss'
          })
        },
        {
          id: PREDICTIONS_SUB_PAGES_IDS.PREDICTION_BALANCE_SHEET,
          route: ROUTES.BALANCE_SHEET,
          linkComponent: intl.formatMessage({
            id: 'financial_fields.type.balance_sheet',
            defaultMessage: 'Balance Sheet'
          })
        },
        {
          id: PREDICTIONS_SUB_PAGES_IDS.PREDICTION_CASH_FLOW,
          route: ROUTES.CASH_FLOW_STATEMENT,
          linkComponent: intl.formatMessage({
            id: 'predict.predictions.cash_flow_statement',
            defaultMessage: 'Cash Flow Statement'
          })
        }
      ];
    }

    if (financialConfig?.accounting_rules === 'french') {
      return [
        ...subtabs.map(tab => {
          const route = encodeURI(deburr(tab.id || tab.label).replace(/ /g, '_'));
          return {
            id: route as PREDICTIONS_SUB_PAGES_IDS,
            route: `/${route}`,
            linkComponent: tab.label || ''
          };
        }),
        {
          id: PREDICTIONS_SUB_PAGES_IDS.PREDICTION_PROFIT_LOSS,
          route: ROUTES.PROFIT_AND_LOSS,
          linkComponent: intl.formatMessage({
            id: 'financial_fields.type.profit_and_loss',
            defaultMessage: 'Profit & Loss'
          })
        },
        {
          id: PREDICTIONS_SUB_PAGES_IDS.PREDICTION_BALANCE_SHEET,
          route: ROUTES.BALANCE_SHEET,
          linkComponent: intl.formatMessage({
            id: 'financial_fields.type.balance_sheet',
            defaultMessage: 'Balance Sheet'
          })
        }
      ];
    }

    return [
      {
        id: PREDICTIONS_SUB_PAGES_IDS.PREDICTIONS_INVOICES,
        route: ROUTES.INVOICES,
        linkComponent: intl.formatMessage({ id: 'predict.invoices', defaultMessage: 'Invoices' })
      },
      ...subtabs.map(tab => {
        const route = encodeURI(deburr(tab.id || tab.label).replace(/[ _]/g, '-'));
        return {
          id: `predictions-${route}` as PREDICTIONS_SUB_PAGES_IDS,
          route: `/${route}`,
          linkComponent: tab.label || tabNames[tab.id || ''] || ''
        };
      }),
      {
        id: PREDICTIONS_SUB_PAGES_IDS.PREDICTION_PROFIT_LOSS,
        route: ROUTES.PROFIT_AND_LOSS,
        linkComponent: intl.formatMessage({
          id: 'financial_fields.type.profit_and_loss',
          defaultMessage: 'Profit & Loss'
        })
      },
      {
        id: PREDICTIONS_SUB_PAGES_IDS.PREDICTION_BALANCE_SHEET,
        route: ROUTES.BALANCE_SHEET,
        linkComponent: intl.formatMessage({
          id: 'financial_fields.type.balance_sheet',
          defaultMessage: 'Balance Sheet'
        })
      },
      {
        id: PREDICTIONS_SUB_PAGES_IDS.PREDICTION_CASH_FLOW,
        route: ROUTES.CASH_FLOW_STATEMENT,
        linkComponent: intl.formatMessage({
          id: 'predict.predictions.cash_flow_statement',
          defaultMessage: 'Cash Flow Statement'
        })
      }
    ];
  }, [financialConfig?.accounting_rules, intl, isConsolidation, subtabs, tabNames]);

  return forecastingPages;
};
